import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiama } from './candidatura.js';

/**
 * L'area di chi legge le candidature.
 *
 * Accesso proprio, separato da tutto il resto: qui entra il personale di La
 * Regina Atlantica, e non c'entra niente con le utenze del CRM.
 */

export type Stato = 'submitted' | 'reviewing' | 'interview' | 'hired' | 'rejected';

export const ETICHETTE: Record<Stato, string> = {
  submitted: 'New',
  reviewing: 'Reviewing',
  interview: 'Interview',
  hired: 'Hired',
  rejected: 'Not selected',
};

export interface RigaElenco {
  id: number;
  status: Stato;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  city: string | null;
  state: string | null;
  positionDesired: string | null;
  submittedAt: string | null;
  resumeCount: number;
}

export interface Personale {
  id: number;
  name: string;
  email: string;
}

export function useChiSono() {
  return useQuery({
    queryKey: ['personale'],
    queryFn: async () => {
      try {
        return (await chiama<{ user: Personale }>('/api/staff/me')).user;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useEntra() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: { email: string; password: string }) =>
      chiama<{ user: Personale }>('/api/staff/login', { metodo: 'POST', corpo: dati }),
    onSuccess: ({ user }) => client.setQueryData(['personale'], user),
  });
}

export function useEsci() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => chiama('/api/staff/logout', { metodo: 'POST' }),
    onSuccess: () => {
      client.setQueryData(['personale'], null);
      client.clear();
    },
  });
}

/**
 * Cambiarsi la password.
 *
 * Il servizio chiude tutte le altre sessioni, non questa: chi la cambia resta
 * dov'è, chiunque fosse entrato con quella vecchia si ritrova fuori.
 */
export function useCambiaPassword() {
  return useMutation({
    mutationFn: (dati: { currentPassword: string; newPassword: string }) =>
      chiama<{ changed: true }>('/api/staff/password', { metodo: 'POST', corpo: dati }),
  });
}

export interface Ricerca {
  q: string;
  status: Stato | '';
  withResume: boolean;
  page: number;
}

export function useCandidature(ricerca: Ricerca) {
  const parametri = new URLSearchParams();
  if (ricerca.q) parametri.set('q', ricerca.q);
  if (ricerca.status) parametri.set('status', ricerca.status);
  if (ricerca.withResume) parametri.set('withResume', 'true');
  parametri.set('page', String(ricerca.page));

  return useQuery({
    queryKey: ['candidature', ricerca],
    queryFn: () =>
      chiama<{ rows: RigaElenco[]; total: number; page: number; pages: number }>(
        `/api/staff/applications?${parametri}`,
      ),
  });
}

export function useConteggi() {
  return useQuery({
    queryKey: ['candidature-conteggi'],
    queryFn: () =>
      chiama<{ counts: { chiave: Stato; etichetta: string; n: number }[] }>('/api/staff/counts'),
  });
}

export interface Scuola {
  kind: string;
  name: string | null;
  yearsCompleted: number | null;
  graduated: boolean | null;
  subjects: string | null;
}

export interface Impiego {
  employerName: string | null;
  jobTitle: string | null;
  phone: string | null;
  fromDate: string | null;
  toDate: string | null;
  reasonForLeaving: string | null;
  dutiesAndSkills: string | null;
  supervisorName: string | null;
  startSalary: string | null;
  finalSalary: string | null;
  address: string | null;
  city: string | null;
  /** «state» nel database è `state_`: la riga arriva così com'è. */
  state_: string | null;
  zip: string | null;
}

export interface Referenza {
  name: string | null;
  role: string | null;
  company: string | null;
  address: string | null;
  addressExtra: string | null;
  phone: string | null;
}

export interface Evento {
  id: number;
  kind: string;
  note: string | null;
  createdAt: string;
  staffName: string | null;
}

/** Una candidatura intera, come la legge chi decide se chiamare. */
export interface CandidaturaCompleta {
  id: number;
  status: Stato;
  submittedAt: string | null;
  firstName: string;
  lastName: string;
  middleInitial: string | null;
  email: string;
  phoneAreaCode: string | null;
  phone: string | null;
  addressStreet: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  isOver18: boolean | null;
  isAuthorizedToWork: boolean | null;
  hasDriverLicense: boolean | null;
  positionDesired: string | null;
  availableFrom: string | null;
  salaryDesired: string | null;
  employedNow: boolean | null;
  mayContactEmployer: boolean | null;
  appliedBefore: boolean | null;
  appliedBeforeWhere: string | null;
  appliedBeforeWhen: string | null;
  referredBy: string | null;
  specialSkills: string | null;
  currentCertifications: string | null;
  militaryService: string | null;
  militaryRank: string | null;
  nationalGuardReserves: string | null;
  convictedOfCrime: boolean | null;
  crimeExplanation: string | null;
  emergencyName: string | null;
  emergencyAddress: string | null;
  emergencyPhone: string | null;
  screeningConsentAt: string | null;
  schools: Scuola[];
  employments: Impiego[];
  references: Referenza[];
  /** `inArchive`: se il file c'è davvero sul disco, non solo la sua riga in tabella. */
  files: { id: number; originalName: string; sizeBytes: number; inArchive: boolean }[];
  events: Evento[];
}

export function useCandidatura(id: number) {
  return useQuery({
    queryKey: ['candidatura', id],
    queryFn: () => chiama<CandidaturaCompleta>(`/api/staff/applications/${id}`),
    enabled: id > 0,
  });
}

export function useCambiaStato(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: { status: Stato; note?: string | null }) =>
      chiama<CandidaturaCompleta>(`/api/staff/applications/${id}/status`, { metodo: 'POST', corpo: dati }),
    onSuccess: (dati) => {
      client.setQueryData(['candidatura', id], dati);
      void client.invalidateQueries({ queryKey: ['candidature'] });
      void client.invalidateQueries({ queryKey: ['candidature-conteggi'] });
    },
  });
}

export function useAnnota(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (note: string) =>
      chiama<CandidaturaCompleta>(`/api/staff/applications/${id}/notes`, { metodo: 'POST', corpo: { note } }),
    onSuccess: (dati) => client.setQueryData(['candidatura', id], dati),
  });
}

export const indirizzoCurriculum = (id: number) => `/api/staff/applications/${id}/resume`;
export const indirizzoStampa = (id: number) => `/api/staff/applications/${id}/print`;
